// BILL-FE-02: the banner of the organisation shell that explains the state of the subscription
// (trial ending or ended, payment late, read-only, suspended, paused, cancellation planned), with
// the owner's call to action and, for the other members, whom to ask.
import { useSubscriptionControllerGet } from '@batiflow/api-client';
import { can } from '@batiflow/client-core';
import { useActiveMembership } from '@batiflow/client-core/react';
import { formatDate } from '@batiflow/i18n';
import { isolate } from '@batiflow/ui';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  __css,
  tokens,
} from '@fluentui/react-components';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/app/providers/i18n-provider';
import { useRefreshSession } from '@/shared/hooks/use-refresh-session';
import {
  SUBSCRIPTION_PATH,
  bannerState,
  formatCount,
  isLockedStatus,
  subscriptionLocksWrites,
  type BannerKind,
} from './model';

const useStyles = __css({"root":{"Bnnss6s":"fi64zpg","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f47sw1k","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f5j02bv"}});

/** The label of the owner's button, by state (null: the state has no action to take). */
const OWNER_ACTION: Readonly<Record<BannerKind, 'choose' | 'pay' | 'resume' | 'withdraw'>> = {
  suspended: 'pay',
  read_only_trial: 'choose',
  read_only_unpaid: 'pay',
  past_due: 'pay',
  paused: 'resume',
  cancelling: 'withdraw',
  cancelled: 'choose',
  trial_ending: 'choose',
};

export interface BillingBannerProps {
  orgId: string;
}

/**
 * Reads the subscription only for members who may (`billing.read`); everyone else is told from the
 * organisation status of the session. When the subscription and the session disagree about
 * whether the office accepts writes (a payment just went through), the session is reloaded once.
 */
export function BillingBanner({ orgId }: BillingBannerProps) {
  const { t } = useTranslation('web');
  const styles = useStyles();
  const navigate = useNavigate();
  const membership = useActiveMembership();
  const refreshSession = useRefreshSession();
  const locale = useLocale();
  const canRead = can(membership?.permissions, 'billing.read');
  const isOwner = can(membership?.permissions, 'billing.manage');
  const organizationStatus = membership?.organizationStatus;

  const subscription = useSubscriptionControllerGet(orgId, {
    query: { enabled: canRead, retry: false, staleTime: 60_000 },
  });
  const data = subscription.data;

  // Resynchronise /me once per version of the subscription that contradicts it.
  const synced = useRef<number | null>(null);
  useEffect(() => {
    if (!data || synced.current === data.version) return;
    if (subscriptionLocksWrites(data.status) === isLockedStatus(organizationStatus)) return;
    synced.current = data.version;
    void refreshSession().catch(() => undefined);
  }, [data, organizationStatus, refreshSession]);

  const state = bannerState(organizationStatus, data, new Date());
  if (!state) return null;

  const goToSubscription = () => {
    void navigate({ to: SUBSCRIPTION_PATH, params: { orgId } });
  };

  const prefix = `billing.banner.${state.kind}`;
  const body = t(`${prefix}.body`, {
    date: state.date ? isolate(formatDate(state.date, locale)) : '',
    count: state.days ?? 0,
    n: formatCount(state.days ?? 0, locale),
  });

  let action: ReactNode = null;
  if (isOwner) {
    // Without an invoice to pay, "pay" becomes "choose a plan".
    const wanted = OWNER_ACTION[state.kind];
    const label = wanted === 'pay' && !state.hasOpenInvoice ? 'choose' : wanted;
    action = (
      <Button appearance="primary" onClick={goToSubscription}>
        {t(`billing.banner.action.${label}`)}
      </Button>
    );
  } else if (canRead) {
    action = <Button onClick={goToSubscription}>{t('billing.banner.action.view')}</Button>;
  }

  return (
    <div className={styles.root} role={state.intent === 'error' ? 'alert' : 'status'}>
      <MessageBar intent={state.intent} layout="multiline">
        <MessageBarBody>
          <MessageBarTitle>{t(`${prefix}.title`)}</MessageBarTitle>
          {body} {t(`${prefix}.${isOwner ? 'owner' : 'member'}`)}
        </MessageBarBody>
        {action ? <MessageBarActions>{action}</MessageBarActions> : null}
      </MessageBar>
    </div>
  );
}


import "/opt/batiflow/app/apps/web/src/features/billing/billing-banner.griffel.css";